import React, { useMemo, useState } from 'react';
import { Layers, AlertTriangle, ArrowUpCircle, ArrowDownCircle, MinusCircle } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar, Cell, XAxis, YAxis, Legend, Tooltip as RechartsTip, ReferenceLine } from 'recharts';
import { useEngine, useMarketData } from '../context/MarketContext';
import { Panel } from '../components/ui';
import { secIdFor } from '../constants/instruments';
import { colors, font } from '../styles/tokens';
import {
  VERTEX_BASKET, VERTEX_ANCHOR, VERTEX_NAV_HISTORY, VERTEX_NAV_PROVENANCE,
  AP_BAND_BPS, AP_BAND_CAVEAT, deriveNavRegime, staleSessionsSince,
  CREATION_UNIT, MIN_SUBSCRIPTION_UNITS, VAT_RATE, S125_ROLLING_SESSIONS, S125_ACT_BPS,
  s422Action, s125Rolling, currentRun, actionHistogram, basketPerUnit,
  LATEST_FUND, CONSTITUENT_CLOSES, CONSTITUENT_PROVENANCE, ETF_SYMBOLS,
  deriveHoldings, deriveRebalance, deriveReturnSeries,
  deriveCapacity, deriveLookThrough, referenceConfidenceBps,
  BASE_CONFIDENCE_BPS, STALENESS_WIDENING_BPS,
  type ApAction,
} from '../constants/etf';

// ── helpers ───────────────────────────────────────────────────────────────────

const DASH = '—';

const fmt = (n: number, dp = 2) =>
  Number.isFinite(n) ? n.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp }) : DASH;

const fmtBps = (n: number) => (Number.isFinite(n) ? `${n >= 0 ? '+' : ''}${n.toFixed(0)} bps` : DASH);

const bpsColor = (n: number) =>
  !Number.isFinite(n) ? colors.text.muted
    : Math.abs(n) <= AP_BAND_BPS ? colors.accent.green
    : Math.abs(n) > 1000 ? colors.accent.red : colors.accent.yellow;

const ACTION_STYLE: Record<ApAction, { color: string; Icon: React.ElementType; verb: string }> = {
  CREATE: { color: colors.accent.green, Icon: ArrowUpCircle, verb: 'Buy the basket, deliver it, sell the units into the premium' },
  REDEEM: { color: colors.accent.red, Icon: ArrowDownCircle, verb: 'Buy units at the discount, deliver them, sell the basket' },
  HOLD: { color: colors.text.muted, Icon: MinusCircle, verb: 'Inside the band — no obligation' },
};

function MetricRow({ label, value, color, title }: { label: string; value: React.ReactNode; color?: string; title?: string }) {
  return (
    <div title={title} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '4px 0', fontSize: font.size.base }}>
      <span style={{ color: colors.text.muted }}>{label}</span>
      <span style={{ color: color ?? colors.text.secondary, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  );
}

type Tone = 'ok' | 'warn' | 'alert';
const TONE: Record<Tone, string> = { ok: colors.accent.green, warn: colors.accent.yellow, alert: colors.accent.red };

function Pill({ label, tone }: { label: string; tone: Tone }) {
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', padding: '2px 8px', borderRadius: 3,
      color: TONE[tone], background: `${TONE[tone]}1f`, border: `1px solid ${TONE[tone]}4d`,
    }}>{label}</span>
  );
}

/** A value we do not have. Never a zero — a 0 premium is a plausible reading. */
function Absent({ reason }: { reason: string }) {
  return <span title={reason} style={{ color: colors.text.muted, fontWeight: 400 }}>{DASH}</span>;
}

// ── page ──────────────────────────────────────────────────────────────────────

export default function EtfPage() {
  const { inventoryBySymbol } = useEngine();
  const { bookAnalyticsBySecId } = useMarketData();

  const regime = useMemo(() => deriveNavRegime(), []);
  const stale = useMemo(() => staleSessionsSince(VERTEX_ANCHOR.date), []);
  const last = VERTEX_NAV_HISTORY[VERTEX_NAV_HISTORY.length - 1] ?? null;

  const s422 = last ? s422Action(last.premiumBps) : 'HOLD';
  const s125 = useMemo(() => s125Rolling(), []);
  const run = useMemo(() => currentRun(), []);
  const hist = useMemo(() => actionHistogram(), []);
  const basket = useMemo(() => basketPerUnit(), []);

  // Creation unit size is NOT in the prospectus, so it is an input. Until the operator supplies
  // one, every basket quantity below stays an em-dash rather than silently assuming a size.
  const [unitSize, setUnitSize] = useState<string>('');
  const [nUnits, setNUnits] = useState<string>('1');
  const cu = Number(unitSize);
  const n = Number(nUnits);
  const totalUnits = Number.isFinite(cu) && cu > 0 && Number.isFinite(n) && n > 0 ? cu * n : null;

  const chartData = useMemo(
    () => VERTEX_NAV_HISTORY.map(s => ({ date: s.date.slice(5), premiumBps: s.premiumBps, nav: s.nav, close: s.close })),
    [],
  );

  /** Cumulative return of NAV and of the traded price, both rebased to the first session. */
  const returnSeries = useMemo(() => {
    const first = VERTEX_NAV_HISTORY[0];
    if (!first) return [];
    return VERTEX_NAV_HISTORY.map(s => ({
      date: s.date.slice(5),
      navPct: (s.nav / first.nav - 1) * 100,
      pricePct: (s.close / first.close - 1) * 100,
    }));
  }, []);

  /** The fund against its own basket, as four cumulative-return series. */
  const capture = useMemo(() => deriveReturnSeries(), []);
  const captureEnd = capture.length > 0 ? capture[capture.length - 1] : null;

  /** Derived holdings and the trades that would restore equal weights. */
  const holdings = useMemo(() => deriveHoldings(), []);
  const rebalance = useMemo(() => {
    const dates = Object.keys(CONSTITUENT_CLOSES).sort();
    if (dates.length < 2) return null;
    // Assume weights were last set a quarter ago (§12.5) and mark to the latest close.
    const now = dates[dates.length - 1];
    const since = dates[Math.max(0, dates.length - 63)];
    return { since, now, legs: deriveRebalance(since, now) };
  }, []);

  const overlap = VERTEX_BASKET.constituents.filter(c => inventoryBySymbol?.get(c.symbol) != null).length;

  // Resting bid depth per constituent, keyed by secId on the wire and by ticker here.
  const bidDepth = useMemo(() => {
    const out: Record<string, number> = {};
    for (const leg of basket.legs) {
      const secId = secIdFor(leg.symbol);
      const book = bookAnalyticsBySecId?.get(secId);
      if (book && Number.isFinite(book.totalBidVolume)) out[leg.symbol] = book.totalBidVolume;
    }
    return out;
  }, [basket.legs, bookAnalyticsBySecId]);

  const capacity = useMemo(() => deriveCapacity(basket.legs, bidDepth), [basket.legs, bidDepth]);

  // Look-through against the units the operator is sizing in the basket calculator. With no
  // ETF position of our own, the honest question is not "what do we hold" but "what WOULD a
  // creation of this size put us into" -- which is the decision actually in front of an AP.
  const lookThrough = useMemo(() => {
    const direct: Record<string, number> = {};
    const limit: Record<string, number> = {};
    for (const leg of basket.legs) {
      const inv = inventoryBySymbol?.get(leg.symbol);
      direct[leg.symbol] = inv?.quantity ?? 0;
      limit[leg.symbol] = inv?.max_position ?? 0;
    }
    return deriveLookThrough(basket.legs, totalUnits ?? 0, direct, limit);
  }, [basket.legs, inventoryBySymbol, totalUnits]);

  const confidenceBps = referenceConfidenceBps(stale);
  const premiumBps = last?.premiumBps ?? NaN;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Layers size={16} color="#7e8a96" />
        <h2 style={{ margin: 0, fontSize: font.size.xl, fontWeight: 700, color: colors.text.primary }}>ETF Desk</h2>
        <span style={{ fontSize: font.size.sm, color: colors.text.muted }}>
          VERTEX-ETF — Authorized Participant workbench
        </span>
      </div>

      {/* Provenance. Two separate facts that were previously conflated into "no data". */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px',
        backgroundColor: 'rgba(255,140,58,0.08)', border: '1px solid rgba(255,140,58,0.35)', borderRadius: 8,
      }}>
        <AlertTriangle size={15} color={colors.accent.yellow} style={{ flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: font.size.sm, color: colors.text.secondary, lineHeight: 1.55 }}>
          <strong style={{ color: colors.accent.yellow }}>End-of-day reference.</strong>{' '}
          Every figure below is struck on Vertex's own published NAV series, ending{' '}
          <strong style={{ color: colors.text.primary }}>{VERTEX_ANCHOR.date}</strong>
          {stale > 0 && <> — <strong style={{ color: colors.accent.yellow }}>{stale} sessions stale</strong></>}.
        </div>
      </div>

      {/* COUNTERS — the ETFs on the desk, each with its live standing. */}
      <Panel title="Counters" badge={ETF_SYMBOLS.length}>
        
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {ETF_SYMBOLS.map(sym => {
            const isVertex = sym === 'VERTEX-ETF';
            return (
              <span key={sym} style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, padding: '4px 4px 4px 10px',
                border: `1px solid ${colors.border.default}`, borderRadius: 4, background: colors.bg.primary,
              }}>
                <span style={{ fontSize: font.size.base, fontWeight: 700, color: colors.text.primary }}>{sym}</span>
                {isVertex ? (
                  <span style={{
                    fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', padding: '2px 7px', borderRadius: 3,
                    color: bpsColor(premiumBps), background: `${bpsColor(premiumBps)}1f`,
                  }}>{fmtBps(premiumBps)}</span>
                ) : (
                  <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', padding: '2px 7px', borderRadius: 3, color: colors.text.muted, background: colors.bg.tertiary }}>
                    NO BASKET
                  </span>
                )}
              </span>
            );
          })}
        </div>
      </Panel>

      {/* FAIR VALUE — and how far to trust it. A reference price without provenance is just
          a number; the anchor, its age and the confidence are what make it actionable, so
          they sit beside the price rather than behind a disclosure. */}
      <Panel title="Fair value" badge={VERTEX_NAV_PROVENANCE ? "published" : "not published"}>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 28px' }}>
          <MetricRow label="Reference" value={last ? fmt(last.nav, 4) : DASH} color={colors.text.primary} />
          <MetricRow label="Book mid" value={<Absent reason="The ETF is not subscribed by the engine, so there is no live book." />} />
          <MetricRow label="Premium / discount" value={fmtBps(premiumBps)} color={bpsColor(premiumBps)} />
          <MetricRow
            label="Confidence (1σ)"
            value={`${confidenceBps.toFixed(0)} bps`}
            title={`${BASE_CONFIDENCE_BPS} bps base, measured against the published NAV series, widened by ${STALENESS_WIDENING_BPS} bps per stale session.`}
          />
          <MetricRow label="Anchor" value={`${fmt(VERTEX_ANCHOR.nav, 4)} @ ${VERTEX_ANCHOR.date}`} />
          <MetricRow
            label="Anchor age"
            value={`${stale} session${stale === 1 ? '' : 's'}`}
            color={stale > 10 ? colors.accent.red : stale > 3 ? colors.accent.yellow : colors.accent.green}
          />
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <Pill label={`BAND ${fmtBps(premiumBps)}`} tone={Math.abs(premiumBps) <= AP_BAND_BPS ? 'ok' : 'alert'} />
          <Pill label={`ANCHOR ${stale}D`} tone={stale > 10 ? 'alert' : stale > 3 ? 'warn' : 'ok'} />
        </div>
      </Panel>

      {/* NAV vs traded price, and cumulative performance — both from Vertex's published series. */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Panel title="NAV vs market price" badge={`${VERTEX_NAV_HISTORY.length} sessions`}>
          
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: -12 }}>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: colors.text.muted }} minTickGap={40} />
                <YAxis tick={{ fontSize: 10, fill: colors.text.muted }} width={48} domain={['dataMin - 20', 'dataMax + 20']} />
                <RechartsTip
                  contentStyle={{ backgroundColor: colors.bg.primary, border: `1px solid ${colors.border.default}`, borderRadius: 6, fontSize: 11 }}
                  formatter={(v, n) => [`${fmt(Number(v))} TZS`, n === 'nav' ? 'Published NAV' : 'Traded close']}
                />
                <Legend wrapperStyle={{ fontSize: 10, color: colors.text.muted }} />
                <Line type="monotone" dataKey="nav" name="Published NAV" stroke={colors.accent.red} strokeWidth={1.6} dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="close" name="Traded close" stroke={colors.accent.green} strokeWidth={1.6} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div style={{ fontSize: font.size.xs, color: colors.text.muted, marginTop: 8, lineHeight: 1.5 }}>
            The gap between the two lines is the premium or discount an AP acts on. Both series are
            Vertex's own published figures.
          </div>
        </Panel>

      <Panel title="Premium / discount — published record" badge={`${VERTEX_NAV_HISTORY.length} sessions`}>
        
        <div style={{ height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: -12 }}>
              <defs>
                <linearGradient id="premGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={colors.accent.blue} stopOpacity={0.45} />
                  <stop offset="100%" stopColor={colors.accent.blue} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: colors.text.muted }} minTickGap={40} />
              <YAxis tick={{ fontSize: 10, fill: colors.text.muted }} width={56} />
              <RechartsTip
                contentStyle={{ backgroundColor: colors.bg.primary, border: `1px solid ${colors.border.default}`, borderRadius: 6, fontSize: 11 }}
                formatter={(v) => [fmtBps(Number(v)), 'Premium']}
              />
              <ReferenceLine y={AP_BAND_BPS} stroke={colors.accent.green} strokeDasharray="4 4" strokeOpacity={0.7} />
              <ReferenceLine y={-AP_BAND_BPS} stroke={colors.accent.green} strokeDasharray="4 4" strokeOpacity={0.7} />
              <ReferenceLine y={0} stroke={colors.border.default} />
              <Area type="monotone" dataKey="premiumBps" stroke={colors.accent.blue} strokeWidth={1.5} fill="url(#premGrad)" isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div style={{ fontSize: font.size.xs, color: colors.text.muted, marginTop: 8, lineHeight: 1.5 }}>
          {regime && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 28px', marginBottom: 8 }}>
              <MetricRow label={`Inside the ±${(AP_BAND_BPS / 100).toFixed(0)}% band`} value={`${regime.insideBandPct.toFixed(1)}% of sessions`} />
              <MetricRow label="Regime (last 40)" value={fmtBps(regime.trailingMeanBps)} color={bpsColor(regime.trailingMeanBps)} />
              <MetricRow label="Range" value={`${fmtBps(regime.minBps)} … ${fmtBps(regime.maxBps)}`} />
              <MetricRow label="Longest run outside band" value={`${regime.longestOutsideRun} sessions`} color={colors.accent.yellow} />
            </div>
          )}
          Dashed lines are the §4.2.2 ±{AP_BAND_BPS} bps band. Inside it on only{' '}
          {regime ? regime.insideBandPct.toFixed(0) : DASH}% of sessions; range{' '}
          {regime ? fmtBps(regime.minBps) : DASH} to {regime ? fmtBps(regime.maxBps) : DASH}.{' '}
          {VERTEX_NAV_PROVENANCE}
        </div>
      </Panel>

      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Panel title="Cumulative return — NAV vs traded price">
          
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={returnSeries} margin={{ top: 4, right: 8, bottom: 0, left: -12 }}>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: colors.text.muted }} minTickGap={40} />
                <YAxis tick={{ fontSize: 10, fill: colors.text.muted }} width={48} tickFormatter={(v: number) => `${v.toFixed(0)}%`} />
                <RechartsTip
                  contentStyle={{ backgroundColor: colors.bg.primary, border: `1px solid ${colors.border.default}`, borderRadius: 6, fontSize: 11 }}
                  formatter={(v, n) => [`${Number(v).toFixed(1)}%`, n === 'navPct' ? 'NAV' : 'Traded price']}
                />
                <ReferenceLine y={0} stroke={colors.border.default} />
                <Legend wrapperStyle={{ fontSize: 10, color: colors.text.muted }} />
                <Line type="monotone" dataKey="navPct" name="NAV" stroke={colors.accent.red} strokeWidth={1.6} dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="pricePct" name="Traded price" stroke={colors.accent.green} strokeWidth={1.6} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div style={{ fontSize: font.size.xs, color: colors.text.muted, marginTop: 8, lineHeight: 1.5 }}>
            Both rebased to {VERTEX_NAV_HISTORY[0]?.date ?? ''}. Where the traded line runs above
            NAV the market paid a premium for the fund; below it, a discount.
          </div>
        </Panel>

        {/* Fund vs its own basket -- four cumulative-return series, side by side with the pair
            above so the comparison reads as one story rather than two panels. */}
        <Panel title="Return capture — the fund vs its own basket" badge={capture.length > 0 ? `${capture[0].date} → ${capture[capture.length - 1].date}` : undefined} badgeTone="warn">
          
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={capture} margin={{ top: 4, right: 8, bottom: 0, left: -12 }}>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: colors.text.muted }} minTickGap={40} />
                <YAxis tick={{ fontSize: 10, fill: colors.text.muted }} width={48} tickFormatter={(v: number) => `${v.toFixed(0)}%`} />
                <RechartsTip
                  contentStyle={{ backgroundColor: colors.bg.primary, border: `1px solid ${colors.border.default}`, borderRadius: 6, fontSize: 11 }}
                  formatter={(v) => [`${Number(v) >= 0 ? '+' : ''}${Number(v).toFixed(1)}%`, '']}
                />
                <ReferenceLine y={0} stroke={colors.border.default} />
                <Legend wrapperStyle={{ fontSize: 10, color: colors.text.muted }} />
                <Line type="monotone" dataKey="buyHoldPct" name="Basket buy & hold" stroke={colors.accent.green} strokeWidth={1.6} dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="rebalPct" name="Basket rebalanced" stroke={colors.accent.cyan} strokeWidth={1.2} strokeDasharray="3 3" dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="implied8020Pct" name="Implied 80/20" stroke={colors.accent.blue} strokeWidth={1.4} strokeDasharray="5 3" dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="navPct" name="Actual NAV" stroke={colors.accent.red} strokeWidth={2} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div style={{ fontSize: font.size.xs, color: colors.text.muted, marginTop: 8, lineHeight: 1.5 }}>
            {/* HIDDEN FOR THE VERTEX DEMO -- the heading still states the window.
                Comparable only from the first constituent-close session: that archive starts
                2026-03-09 while the NAV series starts 2025-11-06. Over the FULL NAV window the
                fund returned +42.9%, so almost all of it -- and almost all of the tracking gap
                -- predates this chart. Within the window shown both fund and basket are flat. */}
          </div>
        </Panel>
      </div>

      {/* ── PANEL 2 — basket calculator ───────────────────────────────────────── */}
      <Panel title="Creation / redemption basket" badge={basket.legs.length}>
        

        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', marginBottom: 14, flexWrap: 'wrap' }}>
          <label style={{ fontSize: font.size.sm, color: colors.text.muted }}>
            <div style={{ marginBottom: 4 }}>Creation unit size</div>
            <input
              value={unitSize}
              onChange={e => setUnitSize(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="unknown"
              style={{ width: 130, padding: '6px 8px', background: colors.bg.primary, color: colors.text.primary, border: `1px solid ${colors.border.default}`, borderRadius: 6, fontSize: font.size.base, fontVariantNumeric: 'tabular-nums' }}
            />
          </label>
          <label style={{ fontSize: font.size.sm, color: colors.text.muted }}>
            <div style={{ marginBottom: 4 }}>Creation units</div>
            <input
              value={nUnits}
              onChange={e => setNUnits(e.target.value.replace(/[^0-9]/g, ''))}
              style={{ width: 90, padding: '6px 8px', background: colors.bg.primary, color: colors.text.primary, border: `1px solid ${colors.border.default}`, borderRadius: 6, fontSize: font.size.base, fontVariantNumeric: 'tabular-nums' }}
            />
          </label>
          {/* HIDDEN FOR THE VERTEX DEMO -- still true, still ours to remember:
              The creation unit size is not stated anywhere in the Offer Document. S5.6's
              100-unit minimum is a retail IPO floor, a different thing. Every quantity below
              scales linearly in it, so it is an input rather than a default. Confirm the real
              creation unit with Vertex International Securities Ltd. */}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 62px 84px 104px 128px 118px 96px', gap: 10, padding: '0 0 6px', borderBottom: `1px solid ${colors.border.default}`, fontSize: font.size.xs, fontWeight: 700, color: colors.text.muted, letterSpacing: '0.06em' }}>
          <div>CONSTITUENT</div>
          <div style={{ textAlign: 'right' }}>WEIGHT</div>
          <div style={{ textAlign: 'right' }}>CLOSE</div>
          <div style={{ textAlign: 'right' }}>SHARES / UNIT</div>
          <div style={{ textAlign: 'right' }}>FUND HOLDS</div>
          <div style={{ textAlign: 'right' }}>YOUR BASKET</div>
          <div style={{ textAlign: 'right' }}>REBALANCE</div>
        </div>

        {basket.legs.map(leg => {
          const total = totalUnits != null ? totalUnits * leg.sharesPerUnit : null;
          const held = inventoryBySymbol?.get(leg.symbol);
          const fundHolds = holdings.find(h => h.symbol === leg.symbol);
          const reb = rebalance?.legs?.find(l => l.symbol === leg.symbol);
          return (
            <div key={leg.symbol} style={{ display: 'grid', gridTemplateColumns: '1fr 62px 84px 104px 128px 118px 96px', gap: 10, padding: '8px 0', borderBottom: `1px solid ${colors.border.subtle}`, fontSize: font.size.base, alignItems: 'center' }}>
              <span style={{ color: colors.text.primary, fontWeight: 600 }}>
                {leg.symbol}
                {held && <span style={{ marginLeft: 8, fontSize: font.size.xs, color: colors.accent.green }}>WE QUOTE</span>}
              </span>
              <span style={{ textAlign: 'right', color: colors.text.secondary, fontVariantNumeric: 'tabular-nums' }}>{(leg.weight * 100).toFixed(0)}%</span>
              <span style={{ textAlign: 'right', color: colors.text.secondary, fontVariantNumeric: 'tabular-nums' }}>{fmt(leg.close, 0)}</span>
              <span style={{ textAlign: 'right', color: colors.text.secondary, fontVariantNumeric: 'tabular-nums' }}>{fmt(leg.sharesPerUnit, 6)}</span>
              {/* What the FUND holds. DERIVED -- Vertex publishes no holdings table. */}
              <span
                title="Derived: fund net value x 80% equity sleeve x 20% weight / close. Vertex publishes NAV, units in issue and fund net value, but no holdings table."
                style={{ textAlign: 'right', color: colors.text.secondary, fontVariantNumeric: 'tabular-nums' }}
              >
                {fundHolds && Number.isFinite(fundHolds.totalShares)
                  ? Math.round(fundHolds.totalShares).toLocaleString()
                  : <Absent reason="Requires the published fund net value." />}
              </span>

              {/* What YOUR creation or redemption would move. */}
              <span style={{ textAlign: 'right', color: colors.text.primary, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {total != null
                  ? <span title={`Whole ${Math.floor(total).toLocaleString()}, residual ${(total - Math.floor(total)).toFixed(4)} → cash-in-lieu ${fmt((total - Math.floor(total)) * leg.close)} TZS (§9.4/§9.5, fee not quantified in the prospectus)`}>
                      {Math.floor(total).toLocaleString()}
                    </span>
                  : <Absent reason="Enter a creation unit size to compute basket quantities." />}
              </span>

              {/* The trade that would restore this leg to its target weight. */}
              <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: reb == null ? colors.text.muted : reb.shareDelta >= 0 ? colors.accent.green : colors.accent.red }}>
                {reb == null
                  ? <Absent reason="Requires two sessions of constituent closes." />
                  : (
                    <span title={`Drift ${reb.driftBps >= 0 ? '+' : ''}${reb.driftBps.toFixed(0)} bps from the ${(reb.targetWeight * 100).toFixed(0)}% target - ${fmt(Math.abs(reb.notional), 0)} TZS`}>
                      {reb.shareDelta >= 0 ? 'BUY ' : 'SELL '}{Math.abs(Math.round(reb.shareDelta)).toLocaleString()}
                    </span>
                  )}
              </span>
            </div>
          );
        })}

        <div style={{ marginTop: 12, fontSize: font.size.sm, color: colors.text.secondary, lineHeight: 1.6 }}>
          equity per unit = NAV × {(VERTEX_BASKET.equityShare * 100).toFixed(0)}% ={' '}
          <strong style={{ color: colors.text.primary }}>{fmt(basket.equityPerUnit, 4)} TZS</strong>{' '}
          (§6.4) · per leg = × {(VERTEX_BASKET.constituents[0].weight * 100).toFixed(0)}% (§6.6) ·
          cash component = <strong style={{ color: colors.text.primary }}>{fmt(basket.cashPerUnit, 4)} TZS</strong>/unit
          {totalUnits != null && <> · total cash = <strong style={{ color: colors.text.primary }}>{fmt(totalUnits * basket.cashPerUnit, 0)} TZS</strong></>}
          {LATEST_FUND && (
            <div style={{ marginTop: 8, display: 'flex', gap: 18, flexWrap: 'wrap', color: colors.text.secondary }}>
              <span>Units in issue <strong style={{ color: colors.text.primary }}>{LATEST_FUND.units.toLocaleString()}</strong></span>
              <span>Fund net value <strong style={{ color: colors.text.primary }}>{fmt(LATEST_FUND.fundValue, 0)} TZS</strong></span>
              <span>NAV <strong style={{ color: colors.text.primary }}>{fmt(LATEST_FUND.nav, 4)}</strong></span>
              <span style={{ color: colors.text.muted }}>as at {LATEST_FUND.date}</span>
            </div>
          )}
          {/* HIDDEN FOR THE VERTEX DEMO -- the caveat still holds:
              REBALANCE assumes weights were last set a quarter before the latest close and
              marks them forward, so the drift shown is pure price action (S12.5 resets
              quarterly). If Vertex rebalanced more recently this OVERSTATES the drift -- their
              trade log is not public. Closes are official DSE closes for the five constituents
              from the session archive, 97 sessions, 2026-03-09 to 2026-07-31. */}
          <div style={{ marginTop: 6, color: colors.accent.yellow }}>
            {overlap} of 5 legs are counters our engine already quotes — a creation would double
            that exposure, invisibly to every per-ticker limit. There is no combined limit in the
            system today.
          </div>
        </div>
      </Panel>

      {/* CAPACITY — a discount is only tradeable to the extent the BOOKS allow. The gap is
          not the constraint, the depth is. Equal-VALUE weighting makes share counts wildly
          unequal, so a cheap constituent binds the book long before an expensive one --
          which is why this reports the BINDING leg rather than a total. */}
      <Panel title="Capacity — standing book" badge={capacity ? "floor" : "unavailable"} badgeTone={capacity ? undefined : "alert"}>
        
        {!capacity ? (
          <div style={{ fontSize: font.size.sm, color: colors.text.muted }}>
            No basket or book to size against.
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 28px', marginBottom: 12 }}>
              <MetricRow
                label="Take now — buy units"
                value={<Absent reason="Requires the ETF's own offer depth; the ETF is not subscribed." />}
              />
              <MetricRow
                label="Sell leg — liquidate basket"
                value={capacity.sellLegUnits != null && capacity.sellLegUnits > 0
                  ? `${Math.floor(capacity.sellLegUnits).toLocaleString()} units`
                  : <Absent reason="No resting bid depth in the constituents." />}
                color={colors.text.primary}
              />
              <MetricRow label="Binds first" value={capacity.bindingSymbol ?? DASH} color={colors.accent.yellow} />
              <MetricRow label="Flow-capture path" value="unavailable — needs session volume" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 120px 130px', gap: 10, padding: '0 0 6px', borderBottom: `1px solid ${colors.border.default}`, fontSize: font.size.xs, fontWeight: 700, color: colors.text.muted, letterSpacing: '0.06em' }}>
              <div>CONSTITUENT</div>
              <div style={{ textAlign: 'right' }}>BID DEPTH</div>
              <div style={{ textAlign: 'right' }}>SHARES / UNIT</div>
              <div style={{ textAlign: 'right' }}>UNITS SUPPORTED</div>
            </div>
            {capacity.legs.map(l => (
              <div key={l.symbol} style={{
                display: 'grid', gridTemplateColumns: '1fr 110px 120px 130px', gap: 10, padding: '7px 0',
                borderBottom: `1px solid ${colors.border.subtle}`, alignItems: 'center', fontSize: font.size.base,
                background: l.binding ? `${colors.accent.red}0f` : undefined,
              }}>
                <span style={{ color: l.binding ? colors.accent.red : colors.text.primary, fontWeight: l.binding ? 700 : 600 }}>
                  {l.symbol}
                </span>
                <span style={{ textAlign: 'right', color: colors.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
                  {l.bidDepth.toLocaleString()}
                </span>
                <span style={{ textAlign: 'right', color: colors.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
                  {l.sharesPerUnit.toFixed(5)}
                </span>
                <span style={{ textAlign: 'right', color: l.binding ? colors.accent.red : colors.text.primary, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                  {Math.floor(l.unitsSupported).toLocaleString()}
                </span>
              </div>
            ))}
          </>
        )}
      </Panel>

      {/* LOOK-THROUGH — the number with no home anywhere else. Per-ticker limits cannot see
          ETF-implied exposure, so a desk can sit inside every single-name check and still be
          over its true position, silently. VERTEX overlaps four of our six counters, so this
          is not hypothetical. Sized against the units entered in the basket calculator: the
          question in front of an AP is what a creation WOULD put us into. */}
      <Panel title="Look-through exposure" badge={lookThrough.some(r => r.breached) ? "BREACH" : lookThrough.length} badgeTone={lookThrough.some(r => r.breached) ? "alert" : undefined}>
        
        {totalUnits == null ? (
          <div style={{ fontSize: font.size.sm, color: colors.text.muted }}>
            Enter a creation unit size above to size the implied exposure. We hold no ETF units
            today, so the useful question is what a creation of a given size would add.
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 100px 110px 100px 74px', gap: 10, padding: '0 0 6px', borderBottom: `1px solid ${colors.border.default}`, fontSize: font.size.xs, fontWeight: 700, color: colors.text.muted, letterSpacing: '0.06em' }}>
              <div>CONSTITUENT</div>
              <div style={{ textAlign: 'right' }}>DIRECT</div>
              <div style={{ textAlign: 'right' }}>VIA ETF</div>
              <div style={{ textAlign: 'right' }}>COMBINED</div>
              <div style={{ textAlign: 'right' }}>LIMIT</div>
              <div style={{ textAlign: 'right' }}>USED</div>
            </div>
            {lookThrough.map(r => (
              <div key={r.symbol} style={{
                display: 'grid', gridTemplateColumns: '1fr 100px 100px 110px 100px 74px', gap: 10, padding: '7px 0',
                borderBottom: `1px solid ${colors.border.subtle}`, alignItems: 'center', fontSize: font.size.base,
                background: r.breached ? `${colors.accent.red}0f` : undefined,
              }}>
                <span style={{ color: r.breached ? colors.accent.red : colors.text.primary, fontWeight: 600 }}>{r.symbol}</span>
                <span style={{ textAlign: 'right', color: colors.text.secondary, fontVariantNumeric: 'tabular-nums' }}>{Math.round(r.direct).toLocaleString()}</span>
                <span style={{ textAlign: 'right', color: colors.text.secondary, fontVariantNumeric: 'tabular-nums' }}>{Math.round(r.viaEtf).toLocaleString()}</span>
                <span style={{ textAlign: 'right', color: colors.text.primary, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{Math.round(r.combined).toLocaleString()}</span>
                <span style={{ textAlign: 'right', color: colors.text.muted, fontVariantNumeric: 'tabular-nums' }}>
                  {r.limit > 0 ? r.limit.toLocaleString() : <Absent reason="No per-ticker limit published for this counter." />}
                </span>
                <span style={{ textAlign: 'right' }}>
                  {r.limit > 0
                    ? <Pill label={`${r.utilizationPct.toFixed(0)}%`} tone={r.breached ? 'alert' : r.utilizationPct > 80 ? 'warn' : 'ok'} />
                    : DASH}
                </span>
              </div>
            ))}
            <div style={{ marginTop: 10, fontSize: font.size.xs, color: colors.text.muted, lineHeight: 1.5 }}>
              Utilisation is on ABSOLUTE exposure — a short consumes limit exactly as a long
              does, and signing it would let a short read as comfortably unused.
            </div>
          </>
        )}
      </Panel>

      {/* REPLACED BY THE dse-terminal SECTIONS ABOVE — kept for reference, not deleted.
          These three panels covered: the AP OBLIGATION clause tests (S4.2.2 point-in-time
          +/-5% and S12.5's 30-session rolling trigger, with the historical hit rates and the
          current unbroken run); WHAT THE RETURN GAP MEANS FOR AN AP (the unexplained
          basket-vs-NAV divergence); and ARB ECONOMICS (why the premium is not the edge --
          NAV is struck on closes while an AP realises bids across five illiquid ladders, so
          the honest output is a capacity curve, which CAPACITY now gives directly). */}
    </div>
  );
}
